import { useState, useCallback, useRef, useEffect } from 'react';
import type { Product } from '@skin-seoul/service';
import { buildFilterUrlString, type FilterUrlState } from '../utils/filterUrlUtils';
import type { FilterOptions } from '../types/filters';

interface UseProductAccumulationProps {
  isMobile: boolean;
  effectiveFilters: FilterOptions;
  sortBy: string;
  effectiveCurrentPage: number;
}

interface UseProductAccumulationReturn {
  accumulatedProducts: Product[];
  accumulatedReviewStatsMap: Record<string, unknown>;
  isLoadingMore: boolean;
  handleProductsLoaded: (products: Product[], reviewStatsMap: Record<string, unknown>) => void;
  handleShowMore: () => void;
  setIsLoadingMore: (loading: boolean) => void;
  resetAccumulation: () => void;
}

/**
 * Custom hook to manage product accumulation for mobile "Show More" functionality
 * Handles:
 * - Accumulating products across pages
 * - Detecting filter/sort changes to reset accumulation
 * - Managing loading states
 * - Preventing duplicate products
 */
export const useProductAccumulation = ({
  isMobile,
  effectiveFilters,
  sortBy,
  effectiveCurrentPage,
}: UseProductAccumulationProps): UseProductAccumulationReturn => {
  const [accumulatedProducts, setAccumulatedProducts] = useState<Product[]>([]);
  const [accumulatedReviewStatsMap, setAccumulatedReviewStatsMap] = useState<Record<string, unknown>>({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const accumulationKeyRef = useRef<string>('');

  const resetAccumulation = useCallback(() => {
    setAccumulatedProducts([]);
    setAccumulatedReviewStatsMap({});
    setIsLoadingMore(false);
  }, []);

  const handleProductsLoaded = useCallback(
    (products: Product[], reviewStatsMap: Record<string, unknown>) => {
      if (!isMobile) {
        return;
      }

      const filterState: FilterUrlState = {
        brands: effectiveFilters.brands || [],
        skinTypes: effectiveFilters.skinTypes || [],
        skinConcerns: effectiveFilters.skinConcerns || [],
        ingredients: effectiveFilters.ingredients || [],
        minPrice: effectiveFilters.minPrice,
        maxPrice: effectiveFilters.maxPrice,
        sortBy,
      };
      const currentKey = buildFilterUrlString(filterState);

      if (currentKey !== accumulationKeyRef.current) {
        accumulationKeyRef.current = currentKey;
        setAccumulatedProducts(products);
        setAccumulatedReviewStatsMap(reviewStatsMap);
        setIsLoadingMore(false);
        return;
      }

      if (effectiveCurrentPage === 1) {
        setAccumulatedProducts(products);
        setAccumulatedReviewStatsMap(reviewStatsMap);
      } else {
        setAccumulatedProducts((prev) => {
          const existingIds = new Set(prev.map((p) => p.productId));
          const newProducts = products.filter((p) => !existingIds.has(p.productId));
          return [...prev, ...newProducts];
        });
        setAccumulatedReviewStatsMap((prev) => ({
          ...prev,
          ...reviewStatsMap,
        }));
      }
      setIsLoadingMore(false);
    },
    [isMobile, effectiveFilters, sortBy, effectiveCurrentPage],
  );

  const handleShowMore = useCallback(() => {
    setIsLoadingMore(true);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      resetAccumulation();
    }
  }, [isMobile, resetAccumulation]);

  return {
    accumulatedProducts,
    accumulatedReviewStatsMap,
    isLoadingMore,
    handleProductsLoaded,
    handleShowMore,
    setIsLoadingMore,
    resetAccumulation,
  };
};
